import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, ExpandMore, ExpandLess } from '@mui/icons-material';
import { IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY = '#0d47a1';

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getDefaultFromDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
};

const fmtAmt = (n) => {
  const v = parseFloat(n) || 0;
  if (Math.abs(v) > 1e15) return '—';
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const safeAmt = (n) => {
  const v = parseFloat(n) || 0;
  return Math.abs(v) > 1e15 ? 0 : v;
};

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};

const GROUP_COLORS = [
  { bg: '#e3f2fd', border: '#90caf9', text: '#0d47a1' },
  { bg: '#f3e5f5', border: '#ce93d8', text: '#6a1b9a' },
  { bg: '#e8f5e9', border: '#a5d6a7', text: '#1b5e20' },
  { bg: '#fff3e0', border: '#ffcc80', text: '#e65100' },
  { bg: '#fce4ec', border: '#f48fb1', text: '#880e4f' },
  { bg: '#e0f7fa', border: '#80deea', text: '#006064' },
  { bg: '#f1f8e9', border: '#aed581', text: '#33691e' },
  { bg: '#fafafa', border: '#bdbdbd', text: '#424242' },
];

const buildGroups = (rows) => {
  const map = new Map();
  rows.forEach(r => {
    const key = r.BalanceSheetHeadId;
    if (!map.has(key)) map.set(key, { headId: key, headName: r.BalanceSheetHeadName, items: [] });
    map.get(key).items.push(r);
  });
  return Array.from(map.values()).sort((a, b) => a.headId - b.headId);
};

const BalanceSheetTView = () => {
  useSessionValidation();
  const { companyId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(fmt2Date(new Date()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('0');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!companyId || !token) return;
    fetchData('BranchApi', `/${companyId}`, token)
      .then(res => {
        if (res.responseCode === '1111') setBranches(res.response_data || []);
        else setBranches(res.response_data || res || []);
      })
      .catch(() => setBranches([]));
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
        if (new Date(fromDate) > new Date(toDate)) {
            setData([]);
            setError('From Date cannot be greater than To Date');
            return;
        }
        fetchReport();
    }
  }, [companyId, token, fromDate, toDate, selectedBranch]); // eslint-disable-line

  const fetchReport = async () => {
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const branchParam = selectedBranch === '0' ? 0 : parseInt(selectedBranch);
      const res = await fetchData(
        'MISReportApi',
        `/BalanceSheetReport/${fromDate}/${toDate}/${companyId}/${branchParam}`,
        token
      );
      const d = res.response_data || res || [];
      setData(d);
      const grps = buildGroups(d);
      const exp = {};
      grps.forEach(g => { exp[g.headId] = true; });
      setExpanded(exp);
    } catch (e) {
      console.error(e);
      setError('Failed to load balance sheet data');
    } finally {
      setLoading(false);
    }
  };

  const groups = buildGroups(data);
  const grandTotal = groups.reduce((s, g) =>
    s + g.items.reduce((ss, r) => ss + safeAmt(r.ClosingBalance), 0), 0);

  const toggleGroup = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => { const e = {}; groups.forEach(g => { e[g.headId] = true; }); setExpanded(e); };
  const collapseAll = () => setExpanded({});

  const selectedBranchObj = branches.find(b => String(b.Id) === String(selectedBranch));
  const selectedBranchName = selectedBranchObj ? (selectedBranchObj.Name || selectedBranchObj.BranchName || '') : '';

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'BalanceSheetTView', 0);
    if (!data.length) return;
    const rows = [];
    groups.forEach(g => {
      const gt = g.items.reduce((s, r) => s + safeAmt(r.ClosingBalance), 0);
      rows.push([g.headName, fmtAmt(gt)]);
      g.items.forEach(r => {
        const v = safeAmt(r.ClosingBalance);
        rows.push([`    ${r.LedgerName}`, v !== 0 ? fmtAmt(v) : '']);
      });
      rows.push([]);
    });
    rows.push(['Grand Total', fmtAmt(grandTotal)]);

    await exportStyledExcel({
      reportTitle: 'BALANCE SHEET — TREE VIEW',
      sheetName: 'BS TView',
      fileName: `BalanceSheet_TView_${toDate}.xlsx`,
      companyName,
      username,
      toDate,
      rawRows: rows,
      columnWidths: [38, 16],
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', padding: 10, fontFamily: "'Inter', sans-serif" }}>
      {loading && <LogoLoader message="Loading..." />}

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '5px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1565c0 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
            Balance Sheet — Tree View
          </span>
          <div style={{ flex: 1 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 27, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, background: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 27, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, background: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
          <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)}
            style={{ height: 27, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0 6px', fontSize: 11, background: 'rgba(255,255,255,0.15)', color: '#fff', colorScheme: 'dark', minWidth: 110 }}>
            <option value="0" style={{ background: '#1e40af' }}>All Branches</option>
            {branches.map(b => (
              <option key={b.Id} value={String(b.Id)} style={{ background: '#1e40af' }}>
                {b.Name || b.BranchName}
              </option>
            ))}
          </select>
          <Tooltip title="Expand All" arrow>
            <IconButton onClick={expandAll} size="small" sx={iconBtn}><ExpandMore sx={{ fontSize: 15 }} /></IconButton>
          </Tooltip>
          <Tooltip title="Collapse All" arrow>
            <IconButton onClick={collapseAll} size="small" sx={iconBtn}><ExpandLess sx={{ fontSize: 15 }} /></IconButton>
          </Tooltip>
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}><Refresh sx={{ fontSize: 15 }} /></IconButton>
          </Tooltip>
          <Tooltip title="Export" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={iconBtn}><GetApp sx={{ fontSize: 15 }} /></IconButton>
          </Tooltip>
        </div>

        {/* Body */}
        <div style={{ padding: '10px 12px' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            {selectedBranch === '0'
              ? <span style={{ fontSize: 13, color: '#64748b' }}>All Branches</span>
              : selectedBranchName
                ? <span style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>{selectedBranchName}</span>
                : null
            }
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Balance Sheet &nbsp;·&nbsp; As on <strong style={{ color: '#475569' }}>{toDate}</strong>
            </div>
          </div>

          {!loading && data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 12 }}>
              No data found for the selected period.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {groups.map((g, gi) => {
                const color = GROUP_COLORS[gi % GROUP_COLORS.length];
                const isOpen = !!expanded[g.headId];
                const groupTotal = g.items.reduce((s, r) => s + safeAmt(r.ClosingBalance), 0);

                return (
                  <div key={g.headId} style={{ border: `1px solid ${color.border}`, borderRadius: 7, overflow: 'hidden' }}>
                    {/* Group header row */}
                    <div
                      onClick={() => toggleGroup(g.headId)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 10px', background: color.bg, cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: color.text, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: color.text }}>{g.headName}</span>
                        <span style={{ fontSize: 10, color: color.text, opacity: 0.7 }}>({g.items.length})</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: color.text }}>₹{fmtAmt(groupTotal)}</span>
                        {isOpen
                          ? <ExpandLess sx={{ fontSize: 15, color: color.text }} />
                          : <ExpandMore sx={{ fontSize: 15, color: color.text }} />
                        }
                      </div>
                    </div>

                    {/* Ledger rows */}
                    {isOpen && (
                      <div>
                        {g.items.map((r, ri) => {
                          const v = safeAmt(r.ClosingBalance);
                          return (
                            <div key={ri} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '4px 10px 4px 22px',
                              background: ri % 2 === 0 ? '#fff' : '#fafbfc',
                              borderTop: '1px solid #f1f5f9',
                            }}>
                              <span style={{ fontSize: 11.5, color: '#374151' }}>{r.LedgerName}</span>
                              <span style={{
                                fontSize: 11.5, fontWeight: v !== 0 ? 600 : 400,
                                color: v === 0 ? '#cbd5e1' : v < 0 ? '#dc2626' : color.text,
                                minWidth: 90, textAlign: 'right',
                              }}>
                                {v === 0 ? '—' : `₹${fmtAmt(v)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Grand Total */}
              {groups.length > 0 && (
                <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1565c0 100%)`, borderRadius: 7, padding: '7px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>Grand Total</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>₹{fmtAmt(grandTotal)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default BalanceSheetTView;
